/**
 * IdentityFederationPeersPageV2 — Stage 3d-frontend.
 *
 * Federation peer management UI. Lists active peers (with a toggle to
 * include retired), supports create/edit via the slideout, soft-retire
 * via the row action, and "Refresh bundle" to touch
 * bundle_last_refreshed_at.
 *
 * Route: /identity/federation-peers (added in App.jsx).
 *
 * Reachable from:
 *   - IdentityLandingPageV2 → Operational views shelf "Federation map"
 *     tile (re-purposed)
 *   - IdentityOrganizationPageV2 → "Open federation map" button
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Network, Plus, RefreshCw, Trash2, Edit3, AlertTriangle, CheckCircle2,
  ArchiveRestore,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { FederationPeerEditSlideout } from '../components/v2/identity/FederationPeerEditSlideout';
import {
  listIdentityFederationPeers,
  retireIdentityFederationPeer,
  refreshIdentityFederationPeerBundle,
} from '../services/api';

const DIRECTION_LABELS = {
  BIDIRECTIONAL: 'Bidirectional',
  INBOUND_ONLY:  'Inbound only',
  OUTBOUND_ONLY: 'Outbound only',
};

function formatBundleAge(iso) {
  if (!iso) return { label: 'Never refreshed', tone: 'danger' };
  const minutesAgo = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutesAgo < 60)   return { label: `${minutesAgo}m ago`,                tone: 'success' };
  if (minutesAgo < 1440) return { label: `${Math.round(minutesAgo / 60)}h ago`, tone: minutesAgo > 720 ? 'warning' : 'success' };
  return { label: 'Expired',                                                  tone: 'danger' };
}

export default function IdentityFederationPeersPageV2() {
  const navigate = useNavigate();

  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [editSlideout, setEditSlideout] = useState({ open: false, peer: null });

  const load = () => {
    setLoading(true);
    setError(null);
    listIdentityFederationPeers({ includeRetired }).then((res) => {
      if (res?.ok === false) {
        setError(res.error || 'Could not load federation peers.');
        setPeers([]);
      } else {
        setPeers(Array.isArray(res?.data) ? res.data : []);
      }
      setLoading(false);
    });
  };

  useEffect(load, [includeRetired]);

  const handleRefresh = async (peer) => {
    const res = await refreshIdentityFederationPeerBundle(peer.id);
    if (res?.ok === false) {
      // eslint-disable-next-line no-console
      console.error('Bundle refresh failed:', res.error);
      return;
    }
    load();
  };

  const handleRetire = async (peer) => {
    if (!window.confirm(`Retire federation peer "${peer.name}"? This sets is_active=false. Use the "Include retired" toggle to view it again.`)) return;
    const res = await retireIdentityFederationPeer(peer.id, { hardDelete: false });
    if (res?.ok === false) {
      // eslint-disable-next-line no-console
      console.error('Retire failed:', res.error);
      return;
    }
    load();
  };

  return (
    <V2PageShell
      title="Federation peers"
      subtitle="External trust domains your org federates with — bidirectional SVID validation across boundaries."
      backTo="/identity"
      backLabel="Back to Identity"
      tags={
        <>
          <Pill tone="trust"  size="sm">Identity</Pill>
          <Pill tone="action" size="sm" dot>SPIFFE plane</Pill>
        </>
      }
      actions={
        <>
          <label className="flex items-center gap-1.5 text-[12px] text-v2-ink-secondary cursor-pointer">
            <input type="checkbox" checked={includeRetired}
                   onChange={(e) => setIncludeRetired(e.target.checked)} />
            Include retired
          </label>
          <Button variant="primary" size="sm" iconLeft={<Plus size={14} />}
                  onClick={() => setEditSlideout({ open: true, peer: null })}>
            New peer
          </Button>
        </>
      }
    >
      {loading && (
        <div className="flex flex-col gap-2">
          <Skeleton height={56} /><Skeleton height={56} /><Skeleton height={56} />
        </div>
      )}

      {!loading && error && (
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title="Could not load federation peers"
            description={error}
            primaryAction={<Button variant="primary" size="sm" onClick={load}>Retry</Button>}
          />
        </Card>
      )}

      {!loading && !error && peers.length === 0 && (
        <Card>
          <EmptyState
            icon={<Network />}
            title="No federation peers yet"
            description={
              includeRetired
                ? 'No peers — active or retired — registered for this org.'
                : 'Federate with another SPIFFE trust domain by registering a peer here.'
            }
            primaryAction={
              <Button variant="primary" size="sm" iconLeft={<Plus size={14} />}
                      onClick={() => setEditSlideout({ open: true, peer: null })}>
                Register first peer
              </Button>
            }
          />
        </Card>
      )}

      {!loading && !error && peers.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          <div className="grid grid-cols-[1.4fr_1.8fr_0.9fr_0.9fr_0.6fr_1.1fr] gap-2 px-4 py-2.5 bg-v2-muted text-[10px] uppercase tracking-wide text-v2-ink-muted font-semibold">
            <div>Name</div>
            <div>SPIFFE ID</div>
            <div>Direction</div>
            <div>Bundle</div>
            <div>Active</div>
            <div></div>
          </div>

          {peers.map((p) => {
            const age = formatBundleAge(p.bundleLastRefreshedAt);
            return (
              <div key={p.id}
                   className={'grid grid-cols-[1.4fr_1.8fr_0.9fr_0.9fr_0.6fr_1.1fr] gap-2 px-4 py-3 border-t border-v2-border-subtle items-center ' +
                              (p.isActive === false ? 'opacity-70' : '')}>
                <div className="text-[13px] font-semibold text-v2-ink truncate">{p.name}</div>
                <div className="font-mono-v2 text-[11.5px] text-v2-ink-secondary truncate" title={p.spiffeId}>
                  {p.spiffeId}
                </div>
                <div>
                  <Pill tone="muted" size="sm">
                    {DIRECTION_LABELS[p.direction] || p.direction}
                  </Pill>
                </div>
                <div className="flex flex-col">
                  <Pill tone={age.tone} size="sm">{age.label}</Pill>
                </div>
                <div>
                  {p.isActive === false
                    ? <Pill tone="muted" size="sm">Retired</Pill>
                    : <Pill tone="success" size="sm">
                        <CheckCircle2 size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />
                        Active
                      </Pill>}
                </div>
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="xs" iconLeft={<RefreshCw size={11} />}
                          onClick={() => handleRefresh(p)}
                          ariaLabel="Refresh bundle">
                    Refresh
                  </Button>
                  <Button variant="ghost" size="xs" iconLeft={<Edit3 size={11} />}
                          onClick={() => setEditSlideout({ open: true, peer: p })}
                          ariaLabel="Edit peer">
                    Edit
                  </Button>
                  {p.isActive !== false && (
                    <Button variant="ghost" size="xs" iconLeft={<Trash2 size={11} />}
                            onClick={() => handleRetire(p)}
                            ariaLabel="Retire peer" />
                  )}
                  {p.isActive === false && (
                    <Button variant="ghost" size="xs" iconLeft={<ArchiveRestore size={11} />}
                            onClick={() => setEditSlideout({ open: true, peer: { ...p, isActive: true } })}
                            ariaLabel="Reactivate peer">
                      Reactivate
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      )}

      <FederationPeerEditSlideout
        isOpen={editSlideout.open}
        peer={editSlideout.peer}
        onClose={() => setEditSlideout({ open: false, peer: null })}
        onSaved={() => { setEditSlideout({ open: false, peer: null }); load(); }}
      />
    </V2PageShell>
  );
}
